import { useState, useEffect } from 'react'
import { postDataAPI } from '../lib/api'
import Button from '../components/ui/Button'
import Alert from '../components/ui/Alert'
import Modal from '../components/ui/Modal'
import { AVAILABLE_SECTIONS, getDefaultPermissions } from '../types/permissions'
import { localDateDisplay } from '../lib/dateUtils'

type TeamMember = {
  _id?: string
  id?: string
  email: string
  name?: string
  role: string
  status: 'active' | 'pending'
  profilePicture?: string
  invitedAt?: string
  expiresAt?: string
}

const roleOptions = [
  {
    value: 'manager',
    label: 'Manager',
    description:
      "Full-featured team member with customizable permissions. Manage the artist's career, handle business affairs, and coordinate with other team members.",
  },
  {
    value: 'support',
    label: 'Support',
    description:
      'View-only access to selected apps. Perfect for assistants, interns, or team members who need limited access to help with specific tasks.',
  },
]

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function getRoleDisplayName(role: string, status: string) {
  if (status === 'pending') return 'Pending'
  switch (role) {
    case 'artist':
      return 'Artist (Owner)'
    case 'manager':
      return 'Manager'
    case 'support':
      return 'Support'
    default:
      return 'Team Member'
  }
}

function getInitials(name: string) {
  if (!name) return '??'
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function getRoleDisplay(role: string) {
  return role === 'manager' ? 'Manager' : role === 'support' ? 'Support' : 'Team Member'
}

export default function TeamManagementPage() {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null)
  const [email, setEmail] = useState('')
  const [selectedRole, setSelectedRole] = useState<'manager' | 'support'>('manager')
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(getDefaultPermissions('manager'))
  const [loadingInvite, setLoadingInvite] = useState(false)
  const [loadingRemove, setLoadingRemove] = useState(false)
  const [confirmEmail, setConfirmEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [openMemberMenu, setOpenMemberMenu] = useState<string | null>(null)

  const [user] = useState<any>(() => {
    try {
      const raw = localStorage.getItem('dizeeUser')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })

  const selectedArtist = localStorage.getItem('selectedArtist')
  const isActualArtist = user?.professionRole === 'artist' && !selectedArtist
  const isTeamMemberDelegated =
    !!selectedArtist && ['manager', 'support'].includes(user?.professionRole || '')
  const artistId = isActualArtist
    ? user?._id
    : (() => {
        try {
          const a = selectedArtist ? JSON.parse(selectedArtist) : null
          return a?._id ?? a?.id ?? null
        } catch {
          return null
        }
      })()

  const token = (() => {
    try {
      const t = localStorage.getItem('dizeeToken')
      if (!t) return null
      const parsed = JSON.parse(t)
      return typeof parsed === 'string' ? parsed : parsed
    } catch {
      return localStorage.getItem('dizeeToken')
    }
  })()

  const fetchTeamMembers = async () => {
    if (!token) return
    try {
      const res = await postDataAPI(
        'team/members',
        { userId: user?._id, userRole: user?.professionRole },
        token
      )
      const data = res?.data?.data ?? res?.data ?? res
      setTeamMembers(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error('Error fetching team members:', e)
      setError('Failed to load team members')
    }
  }

  useEffect(() => {
    fetchTeamMembers()
  }, [user?._id, user?.professionRole])

  // Update permissions when role changes
  const handleRoleChange = (newRole: 'manager' | 'support') => {
    setSelectedRole(newRole)
    setSelectedPermissions(getDefaultPermissions(newRole))
  }

  const togglePermission = (sectionId: string) => {
    setSelectedPermissions(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    )
  }

  const handleInvite = async () => {
    const trimmed = email.trim()
    if (!trimmed) {
      setError('Please enter an email address')
      return
    }
    if (!isValidEmail(trimmed)) {
      setError('Please enter a valid email address')
      return
    }
    if (selectedPermissions.length === 0) {
      setError('Please select at least one section access permission')
      return
    }
    if (!token || !artistId) {
      setError('Unable to send invitation')
      return
    }
    setError(null)
    setLoadingInvite(true)
    try {
      const res = await postDataAPI(
        'team/invite',
        {
          email: trimmed,
          role: selectedRole,
          accountType: selectedRole,
          inviterRole: user?.professionRole,
          inviterId: user?._id,
          artistId,
          permissions: { sections: selectedPermissions },
        },
        token
      )
      if (res?.status === 200 || res?.data?.status === 200) {
        setSuccess('Team invitation sent successfully!')
        setEmail('')
        setSelectedRole('manager')
        setSelectedPermissions(getDefaultPermissions('manager'))
        setIsInviteModalOpen(false)
        fetchTeamMembers()
      } else {
        setError((res?.message ?? res?.data?.message) || 'Failed to send invitation')
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ?? err?.message ?? 'Failed to send invitation'
      )
    } finally {
      setLoadingInvite(false)
    }
  }

  const handleOpenInviteModal = () => {
    setEmail('')
    setSelectedRole('manager')
    setSelectedPermissions(getDefaultPermissions('manager'))
    setError(null)
    setIsInviteModalOpen(true)
  }

  const handleOpenEditModal = (member: TeamMember) => {
    setEditingMember(member)
    // Load member's existing permissions
    const memberPermissions = (member as any).permissions?.sections || getDefaultPermissions(member.role as 'manager' | 'support')
    setSelectedRole(member.role as 'manager' | 'support')
    setSelectedPermissions(memberPermissions)
    setError(null)
    setIsEditModalOpen(true)
    setOpenMemberMenu(null)
  }

  const handleUpdateMember = async () => {
    if (!editingMember || !token) return
    
    if (selectedPermissions.length === 0) {
      setError('Please select at least one section access permission')
      return
    }

    setLoadingInvite(true)
    setError(null)
    
    try {
      const res = await postDataAPI(
        'team/update-member-permissions',
        {
          memberId: editingMember._id || editingMember.id,
          artistId,
          permissions: { sections: selectedPermissions },
        },
        token
      )
      
      if (res?.status === 200 || res?.data?.status === 200) {
        setSuccess('Team member permissions updated successfully!')
        setIsEditModalOpen(false)
        setEditingMember(null)
        fetchTeamMembers()
      } else {
        setError((res?.message ?? res?.data?.message) || 'Failed to update permissions')
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ?? err?.message ?? 'Failed to update permissions'
      )
    } finally {
      setLoadingInvite(false)
    }
  }

  const handleRemoveMember = async (memberEmail: string) => {
    if (!isActualArtist || !token) {
      setError('Only artists can remove team members')
      return
    }
    setLoadingRemove(true)
    setError(null)
    try {
      const res = await postDataAPI(
        'team/remove-member',
        { memberEmail, artistId: user?._id },
        token
      )
      if (res?.status === 200 || res?.data?.status === 200) {
        setSuccess('Team member removed successfully')
        setConfirmEmail('')
        fetchTeamMembers()
      } else {
        setError((res?.message ?? res?.data?.message) || 'Failed to remove team member')
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ?? err?.message ?? 'Failed to remove team member'
      )
    } finally {
      setLoadingRemove(false)
    }
  }

  const handleCancelInvitation = async (invitedEmail: string) => {
    if (!isActualArtist || !token) {
      setError('Only artists can cancel invitations')
      return
    }
    setLoadingRemove(true)
    setError(null)
    try {
      const res = await postDataAPI(
        'team/cancel-invitation',
        { invitedEmail, artistId: user?._id },
        token
      )
      if (res?.status === 200 || res?.data?.status === 200) {
        setSuccess('Invitation cancelled successfully')
        setConfirmEmail('')
        fetchTeamMembers()
      } else {
        setError((res?.message ?? res?.data?.message) || 'Failed to cancel invitation')
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ?? err?.message ?? 'Failed to cancel invitation'
      )
    } finally {
      setLoadingRemove(false)
    }
  }

  const activeMembers = teamMembers.filter((m) => m.status === 'active')
  const pendingInvitations = teamMembers.filter((m) => m.status === 'pending')

  if (!user) {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      </div>
    )
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-10 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2 text-gray-900 dark:text-white">
            Team Management
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Invite managers and support staff to help manage your career
          </p>
        </div>
        {isActualArtist && (
          <Button onClick={handleOpenInviteModal}>
            Invite Team Member
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-6">
          <Alert type="error" message={error} onDismiss={() => setError(null)} />
        </div>
      )}
      {success && (
        <div className="mb-6">
          <Alert type="success" message={success} onDismiss={() => setSuccess(null)} />
        </div>
      )}

      {!isActualArtist && isTeamMemberDelegated && (
        <div className="bg-gray-50 dark:bg-gray-800/80 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-10">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            You can view team members. Only the artist can invite or remove team members.
          </p>
        </div>
      )}

      {/* Active Team Members */}
      {activeMembers.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-visible mb-10">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-sm font-medium text-gray-900 dark:text-white">
              Team Members ({activeMembers.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700 overflow-visible">
            {activeMembers.map((member) => {
              const gradient = 'linear-gradient(45deg, #8b5cf6, #a78bfa)'
              return (
                <div
                  key={member.email + (member._id ?? member.id ?? '')}
                  className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                      style={{ background: gradient }}
                    >
                      {member.profilePicture ? (
                        <img
                          src={member.profilePicture}
                          alt=""
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        getInitials(member.name || member.email)
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {member.name || member.email || 'Unnamed User'}
                      </div>
                      {member.name && member.name !== member.email && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {member.email}
                        </div>
                      )}
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 mt-1">
                        {getRoleDisplayName(member.role, member.status)}
                      </span>
                    </div>
                  </div>
                           {isActualArtist && member.role !== 'artist' && (
                             <div className="relative">
                               <button
                                 type="button"
                                 onClick={() => setOpenMemberMenu(openMemberMenu === member.email ? null : member.email)}
                                 className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:ring-offset-1 rounded p-1 transition-colors"
                               >
                                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                 </svg>
                               </button>
                               {openMemberMenu === member.email && (
                                 <>
                                   <div className="fixed inset-0 z-30" onClick={() => setOpenMemberMenu(null)} />
                                   <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-40 py-1">
                                     <button
                                       type="button"
                                       onClick={() => handleOpenEditModal(member)}
                                       className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                     >
                                       Edit Permissions
                                     </button>
                                     <button
                                       type="button"
                                       onClick={() => {
                                         setConfirmEmail(member.email)
                                         setOpenMemberMenu(null)
                                       }}
                                       className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                     >
                                       Remove Member
                                     </button>
                                   </div>
                                 </>
                               )}
                             </div>
                           )}
                  {(!isActualArtist || member.role === 'artist') && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {member.role === 'artist' ? 'Owner' : getRoleDisplayName(member.role, member.status)}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-sm font-medium text-gray-900 dark:text-white">
              Pending Invitations ({pendingInvitations.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {pendingInvitations.map((inv) => (
              <div
                key={inv.email}
                className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {inv.email}
                  </span>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {getRoleDisplayName(inv.role, 'pending')} •{' '}
                    Invited {inv.invitedAt ? localDateDisplay(inv.invitedAt) : '—'}
                  </div>
                </div>
                {isActualArtist ? (
                  confirmEmail === inv.email ? (
                    <button
                      type="button"
                      onClick={() => handleCancelInvitation(inv.email)}
                      disabled={loadingRemove}
                      className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium transition-colors"
                    >
                      {loadingRemove ? 'Cancelling…' : 'Confirm Cancel'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmEmail(inv.email)}
                      className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  )
                ) : (
                  <span className="text-xs text-gray-500 dark:text-gray-400">Pending</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {teamMembers.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">No team members yet</p>
          {isActualArtist && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              Click "Invite Team Member" above to invite your first team member
            </p>
          )}
        </div>
      )}

      {/* Invite Team Member Modal */}
      <Modal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        title="Invite Team Member"
        description="Set up access permissions for your new team member"
        maxWidthClassName="max-w-2xl"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            handleInvite()
          }}
          className="space-y-6"
        >
          {/* Email Input */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Email Address
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="team@example.com"
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent"
            />
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Role
            </label>
            <div className="grid grid-cols-1 gap-3">
              {roleOptions.map((role) => {
                const isSelected = selectedRole === role.value
                return (
                  <label
                    key={role.value}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-750'
                        : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 opacity-60 hover:opacity-80'
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={role.value}
                      checked={isSelected}
                      onChange={(e) => handleRoleChange(e.target.value as 'manager' | 'support')}
                      className="mt-0.5 w-4 h-4 text-gray-900 dark:text-gray-400 border-gray-300 dark:border-gray-600"
                    />
                    <div className="flex-1">
                      <div className={`text-sm font-semibold mb-1 transition-colors ${
                        isSelected ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-500'
                      }`}>
                        {role.label}
                      </div>
                      <div className={`text-xs transition-colors ${
                        isSelected ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-600'
                      }`}>
                        {role.description}
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Section Permissions */}
          <div>
            <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">
              Section Access
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                ({selectedPermissions.length} selected)
              </span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto p-1">
              {AVAILABLE_SECTIONS.map((section) => {
                const isSelected = selectedPermissions.includes(section.id)
                return (
                  <label
                    key={section.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-750'
                        : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 opacity-60 hover:opacity-80'
                    }`}
                  >
                    <div className="flex-1">
                      <div className={`text-sm transition-colors ${
                        isSelected ? 'text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-500 dark:text-gray-500'
                      }`}>
                        {section.label}
                      </div>
                      <div className={`text-xs mt-0.5 transition-colors ${
                        isSelected ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400 dark:text-gray-600'
                      }`}>
                        {section.description}
                      </div>
                    </div>
                    
                    {/* Styled Checkbox */}
                    <div className="flex-shrink-0 relative flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => togglePermission(section.id)}
                        className={`w-4 h-4 rounded border-2 transition-all duration-200 ease-in-out appearance-none cursor-pointer focus:ring-2 focus:ring-offset-0 ${
                          isSelected
                            ? 'bg-gray-900 dark:bg-gray-100 border-gray-900 dark:border-gray-100'
                            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-gray-900 dark:focus:ring-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      />
                      {isSelected && (
                        <svg 
                          className="absolute w-3 h-3 pointer-events-none dark:hidden checkbox-check" 
                          viewBox="0 0 20 20" 
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                            fill="white"
                          />
                        </svg>
                      )}
                      {isSelected && (
                        <svg 
                          className="absolute w-3 h-3 pointer-events-none hidden dark:block checkbox-check" 
                          viewBox="0 0 20 20" 
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                            fill="#111111"
                          />
                        </svg>
                      )}
                    </div>
                  </label>
                )
              })}
            </div>
            {selectedPermissions.length === 0 && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                Please select at least one section
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="submit"
              disabled={loadingInvite || selectedPermissions.length === 0}
              className="flex-1"
            >
              {loadingInvite ? 'Sending Invitation…' : 'Send Invitation'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsInviteModalOpen(false)}
              disabled={loadingInvite}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Team Member Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Team Member Permissions"
        description={`Update section access for ${editingMember?.name || editingMember?.email}`}
        maxWidthClassName="max-w-2xl"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            handleUpdateMember()
          }}
          className="space-y-6"
        >
          {/* Member Info */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
              style={{ background: 'linear-gradient(45deg, #8b5cf6, #a78bfa)' }}
            >
              {editingMember?.profilePicture ? (
                <img
                  src={editingMember.profilePicture}
                  alt=""
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                getInitials(editingMember?.name || editingMember?.email || '')
              )}
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {editingMember?.name || editingMember?.email}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {getRoleDisplay(editingMember?.role || '')}
              </div>
            </div>
          </div>

          {/* Section Permissions */}
          <div>
            <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">
              Section Access
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                ({selectedPermissions.length} selected)
              </span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto p-1">
              {AVAILABLE_SECTIONS.map((section) => {
                const isSelected = selectedPermissions.includes(section.id)
                return (
                  <label
                    key={section.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-750'
                        : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 opacity-60 hover:opacity-80'
                    }`}
                  >
                    <div className="flex-1">
                      <div className={`text-sm transition-colors ${
                        isSelected ? 'text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-500 dark:text-gray-500'
                      }`}>
                        {section.label}
                      </div>
                      <div className={`text-xs mt-0.5 transition-colors ${
                        isSelected ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400 dark:text-gray-600'
                      }`}>
                        {section.description}
                      </div>
                    </div>
                    
                    {/* Styled Checkbox */}
                    <div className="flex-shrink-0 relative flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => togglePermission(section.id)}
                        className={`w-4 h-4 rounded border-2 transition-all duration-200 ease-in-out appearance-none cursor-pointer focus:ring-2 focus:ring-offset-0 ${
                          isSelected
                            ? 'bg-gray-900 dark:bg-gray-100 border-gray-900 dark:border-gray-100'
                            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-gray-900 dark:focus:ring-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      />
                      {isSelected && (
                        <svg 
                          className="absolute w-3 h-3 pointer-events-none dark:hidden checkbox-check" 
                          viewBox="0 0 20 20" 
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                            fill="white"
                          />
                        </svg>
                      )}
                      {isSelected && (
                        <svg 
                          className="absolute w-3 h-3 pointer-events-none hidden dark:block checkbox-check" 
                          viewBox="0 0 20 20" 
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                            fill="#111111"
                          />
                        </svg>
                      )}
                    </div>
                  </label>
                )
              })}
            </div>
            {selectedPermissions.length === 0 && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                Please select at least one section
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="submit"
              disabled={loadingInvite || selectedPermissions.length === 0}
              className="flex-1"
            >
              {loadingInvite ? 'Updating…' : 'Update Permissions'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsEditModalOpen(false)}
              disabled={loadingInvite}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* Remove Member Confirmation Modal */}
      {confirmEmail && (
        <Modal
          isOpen={true}
          onClose={() => setConfirmEmail('')}
          title="Remove Team Member"
          description="Are you sure you want to remove this team member?"
          maxWidthClassName="max-w-md"
        >
          <div className="space-y-6">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong className="text-gray-900 dark:text-white">{confirmEmail}</strong> will lose access to your account immediately. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => handleRemoveMember(confirmEmail)}
                disabled={loadingRemove}
                className="flex-1 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
              >
                {loadingRemove ? 'Removing…' : 'Remove Member'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setConfirmEmail('')}
                disabled={loadingRemove}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
